// src/components/proposals/TemplatesTab.jsx
//
// Proposal Templates — the master/default content for each service tier.
// A template is the starting point every new proposal is stamped from; editing
// one changes what FUTURE proposals begin with and never reaches back into
// existing ones (a proposal deep-clones the defaults at creation).
//
// Deliberately unversioned: templates are living documents, unlike proposals
// which carry immutable snapshots of what a customer was actually sent.
import React, { useState, useCallback } from "react";
import { templateComponentFor } from "./templateRegistry";
import { Panel, SettingsPanel } from "./ProposalSettingsPanel";
import LeadSettingsPanel from "./LeadSettingsPanel";
import { isLead } from "./templateDefaults";

const fmtDate = (ts) =>
  ts ? new Date(ts).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—";

const TIER_COLOR = {
  "bronze-sentinel":  "#92400e",
  "silver-guardian":  "#64748b",
  "gold-fortress":    "#0b80a5",
  // Not a tier — the Lead pre-proposal, which covers all four. Deliberately a
  // colour no tier uses, so its card doesn't read as one of them.
  "lead-preproposal": "#7c3aed",
};

export default function TemplatesTab({ proposalTemplates = [], setProposalTemplates,
                                       saveSnapshot, defaultMarkupPct = 0, onCatalogSearch }) {
  const [openId, setOpenId] = useState(null);

  // Same updater-form discipline as ProposalsTab — paired writes must compose.
  const commit = useCallback((updater) => {
    setProposalTemplates((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      saveSnapshot({ proposalTemplates: next });
      return next;
    });
  }, [setProposalTemplates, saveSnapshot]);

  const updateTemplateContent = (templateId, patch) =>
    commit((prev) => prev.map((t) => t.id !== templateId ? t : {
      ...t,
      defaultContent: { ...t.defaultContent, ...patch },
      updatedAt: Date.now(),
    }));

  const open = proposalTemplates.find((t) => t.id === openId) || null;

  /* ── Dashboard ───────────────────────────────────────────────────────── */
  if (!open) {
    return (
      <div style={{ padding: "0 4px", width: "100%", flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 6 }}>
          <div style={{ fontFamily: "var(--ff-head)", fontWeight: 700, fontSize: 17,
                        color: "var(--text)", letterSpacing: "-0.3px" }}>Proposal Templates</div>
        </div>
        <div style={{ fontSize: 12, color: "var(--text3)", marginBottom: 16, maxWidth: 720 }}>
          The master copy for each service tier, plus the Lead pre-proposal sent ahead of them.
          Editing a template sets the starting point for new documents — existing ones keep
          whatever they were created with.
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(310px, 1fr))",
                      gap: 14 }}>
          {proposalTemplates.map((t) => (
            <TemplateCard key={t.id} t={t} onEdit={() => setOpenId(t.id)} />
          ))}
        </div>
      </div>
    );
  }

  /* ── Editor ──────────────────────────────────────────────────────────── */
  // Preview renders read-only: everything a template owns is editable from the
  // settings pane, and inline editing would invite typing customer-specific
  // text into a master document.
  //
  // The Lead template's content is a 4-tier matrix rather than one tier's
  // narrative, so it gets its own pane. SettingsPanel here would render Bronze's
  // pricing and page-visibility controls against fields a Lead does not have.
  const OpenSettingsPanel = isLead(open) ? LeadSettingsPanel : SettingsPanel;

  return (
    <div style={{ padding: "0 4px", width: "100%", flex: 1, minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
        <button className="btn" style={{ fontSize: 11 }} onClick={() => setOpenId(null)}>
          ← All Templates
        </button>
        <div style={{ fontFamily: "var(--ff-head)", fontWeight: 700, fontSize: 15, color: "var(--text)" }}>
          {open.name}
        </div>
        <span style={{ fontSize: 10, color: "var(--text3)" }}>
          Last updated {fmtDate(open.updatedAt)}
        </span>
      </div>

      {/* The distinction that matters — this is the master, not a customer's copy. */}
      <div style={{ padding: "10px 16px", background: "var(--s2)",
                    border: "1px solid var(--strat)", borderLeft: "4px solid var(--strat)",
                    borderRadius: 6, fontSize: 12, marginBottom: 12, color: "var(--text2)" }}>
        📋 You're editing the <strong style={{ color: "var(--text)" }}>{open.name}</strong> master
        template. Changes here set the default starting point for new proposals — existing
        proposals are not affected.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(300px, 35%) 1fr", gap: 14,
                    alignItems: "start" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12,
                      maxHeight: "calc(100vh - 240px)", overflowY: "auto", paddingRight: 4 }}>
          <OpenSettingsPanel
            p={{ content: open.defaultContent }}
            onContent={(patch) => updateTemplateContent(open.id, patch)}
            hideCustomerFields
            markupPct={defaultMarkupPct}
            onCatalogSearch={onCatalogSearch}
          />
          <Panel title="About this template">
            <div style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.6 }}>
              {isLead(open)
                ? `Customer name, contact, date and the cover image aren't stored here — they're
                   filled in per Lead. Everything else on this page (the plan blurbs and rates,
                   the feature comparison, the add-ons and the terms) becomes the default for
                   the next Lead you create.`
                : `Customer name, contact, dates, device/user counts and the cover image aren't
                   stored here — they're filled in per proposal. Everything else on this page
                   becomes the default for the next proposal you create from ${open.name}.`}
            </div>
          </Panel>
        </div>

        <div style={{ background: "#b8b8b8", borderRadius: 10, padding: 16, overflow: "auto",
                      maxHeight: "calc(100vh - 240px)" }}>
          <div style={{ width: 794, margin: "0 auto" }}>
            {(() => { const T = templateComponentFor(open.key);
                      return <T content={open.defaultContent} mode="view" />; })()}
          </div>
        </div>
      </div>
    </div>
  );
}

function TemplateCard({ t, onEdit }) {
  const comingSoon = t.status === "coming-soon";
  return (
    <div style={{ background: "var(--s1)", border: "1px solid var(--border)", borderRadius: 10,
                  overflow: "hidden", display: "flex", flexDirection: "column",
                  opacity: comingSoon ? 0.62 : 1 }}>
      <div style={{ height: 3, background: TIER_COLOR[t.key] || "#0b80a5" }} />
      <div style={{ padding: 14, flex: 1 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                      gap: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: "var(--text)", letterSpacing: "-0.2px" }}>
            {t.name}
          </div>
          <span style={{ fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: 20,
                         textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap",
                         background: comingSoon ? "#f1f5f9" : "#dcfce7",
                         color: comingSoon ? "#94a3b8" : "#15803d" }}>
            {comingSoon ? "Coming soon" : "Active"}
          </span>
        </div>
        <div style={{ fontSize: 11, color: "var(--text3)", marginBottom: 12 }}>{t.tagline}</div>

        {comingSoon ? (
          <div style={{ fontSize: 11, color: "var(--text3)", lineHeight: 1.6,
                        padding: "8px 10px", background: "var(--s2)", borderRadius: 6,
                        border: "1px dashed var(--border)" }}>
            Upload your Claude Designs source to activate this tier.
          </div>
        ) : (
          <div style={{ fontSize: 10, color: "var(--text3)" }}>
            Updated {fmtDate(t.updatedAt)}
          </div>
        )}
      </div>
      <div style={{ padding: "10px 14px", borderTop: "1px solid var(--border)" }}>
        <button className="btn btn-strat" style={{ width: "100%", fontSize: 10 }}
                disabled={comingSoon} onClick={comingSoon ? undefined : onEdit}>
          {comingSoon ? "Not available yet" : "Edit defaults"}
        </button>
      </div>
    </div>
  );
}
